package br.com.vvrefeicoes.portal.domain.model

/**
 * O "Resumo da Folha" não é uma tabela: é um relatório paginado, com um bloco por departamento
 * (cabeçalho com empresa, CNPJ e departamento, seguido das verbas e do total). Converte o relatório
 * em uma grade com cabeçalho e uma linha por verba, nas colunas de [RhPayrollMassColumn]. A linha
 * "Total dos Eventos" fica de fora, por ser a soma das verbas.
 *
 * Uma grade que não tenha o formato do relatório é devolvida como veio.
 */
fun payrollMassReportToGrid(grid: List<List<String>>): List<List<String>> {
    val rows = ArrayList<List<String>>()
    var company = ""
    var cnpj = ""
    var department = ""
    var layout: PayrollMassLayout? = null
    // Só o relatório identifica o departamento por rótulo; numa planilha já tabular ele é coluna.
    var departmentLabelFound = false
    grid.forEach { row ->
        if (row.all { it.isBlank() }) return@forEach
        val current = layout
        val code = if (current == null) "" else row.cell(current.code)
        if (current != null && code.isNotEmpty() && code.all { it.isDigit() }) {
            rows +=
                RhPayrollMassColumn.entries.map { column ->
                    when (column) {
                        RhPayrollMassColumn.EMPRESA -> company
                        RhPayrollMassColumn.CNPJ -> cnpj
                        RhPayrollMassColumn.DEPARTAMENTO -> department
                        RhPayrollMassColumn.UNIDADE -> ""
                        RhPayrollMassColumn.CODIGO -> code
                        RhPayrollMassColumn.DESCRICAO -> row.valueAfter(current.code)
                        RhPayrollMassColumn.ADICIONAIS -> row.cell(current.additions)
                        RhPayrollMassColumn.DESCONTOS -> row.cell(current.discounts)
                        RhPayrollMassColumn.QTD_FUNCIONARIOS -> row.cell(current.employees)
                    }
                }
            return@forEach
        }
        val keys = row.map(::headerKey)
        when {
            COMPANY_LABEL in keys -> {
                company = row.valueAfter(keys.indexOf(COMPANY_LABEL))
                cnpj = ""
                department = ""
            }
            CNPJ_LABEL in keys -> cnpj = row.valueAfter(keys.indexOf(CNPJ_LABEL))
            DEPARTMENT_LABEL in keys -> {
                department = row.valueAfter(keys.indexOf(DEPARTMENT_LABEL))
                departmentLabelFound = true
            }
            CODE_LABEL in keys && ADDITIONS_LABEL in keys -> layout = PayrollMassLayout.of(keys)
        }
    }
    if (!departmentLabelFound || rows.isEmpty()) return grid
    return listOf(RhPayrollMassColumn.entries.map { it.header }) + rows
}

/** Posição das colunas de valores, lida da linha de cabeçalho de cada bloco do relatório. */
private data class PayrollMassLayout(
    val code: Int,
    val additions: Int,
    val discounts: Int,
    val employees: Int,
) {
    companion object {
        fun of(keys: List<String>) =
            PayrollMassLayout(
                code = keys.indexOf(CODE_LABEL),
                additions = keys.indexOf(ADDITIONS_LABEL),
                discounts = keys.indexOf(DISCOUNTS_LABEL),
                employees = keys.indexOf(EMPLOYEES_LABEL),
            )
    }
}

/** Primeira célula preenchida depois de [index]: o relatório deixa colunas vazias entre o rótulo e o valor. */
private fun List<String>.valueAfter(index: Int): String = drop(index + 1).firstOrNull { it.isNotBlank() }?.trim().orEmpty()

private fun List<String>.cell(index: Int): String = getOrElse(index) { "" }.trim()

private const val COMPANY_LABEL = "empresa"
private const val CNPJ_LABEL = "cnpj cei"
private const val DEPARTMENT_LABEL = "depto"
private const val CODE_LABEL = "codigo"
private const val ADDITIONS_LABEL = "adicionais"
private const val DISCOUNTS_LABEL = "descontos"
private const val EMPLOYEES_LABEL = "qtd funcionarios"
